import {zodResolver} from "@hookform/resolvers/zod";
import {
  MAX_PROPOSALS,
  MAX_TRAVEL_TIME_MINUTES,
  MaxTravelTimeMinutesSchema,
  RDV_DEFAULT_MAX_TRAVEL_DISTANCE_KM,
  RDV_DEFAULT_MAX_TRAVEL_TIME_MINUTES,
  RDV_HORIZON_DAYS,
  RDV_PROPOSAL_LIMIT,
  RDV_ROUNDING_MINUTES,
  type UpdateRdvProposalLimitPayload,
  UpdateRdvProposalLimitPayloadSchema,
} from "@mytour/shared";
import {FloatingUnitField} from "@mytour/ui/form";
import {Controller, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Button, ButtonGroup, Form, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {
  useRdvPlanningSetting,
  useUpdateRdvProposalLimit,
  useUpdateRdvTravelSetting,
} from "../../query/rdvPlanningSettingQueries";

const travelSettingsFormSchema = z.object({
  // The pro picks one travel-time value (chips); V1 applies it to all three edges.
  travelTimeMinutes: MaxTravelTimeMinutesSchema,
  // Null = "use the default" (shown grayed-out); resolved to the default on submit.
  // Bounds mirror MaxTravelDistanceKmSchema (@mytour/shared) but carry French
  // messages so an invalid manual entry shows actionable inline feedback.
  maxDistanceKm: z
    .number({invalid_type_error: "Distance invalide"})
    .int("La distance doit être un nombre entier")
    .min(1, "La distance doit être d'au moins 1 km")
    .max(2000, "La distance ne peut pas dépasser 2000 km")
    .nullable(),
});
type TravelSettingsFormData = z.infer<typeof travelSettingsFormSchema>;

/**
 * RDV en ligne — travel constraints (Story 1.3, #506).
 *
 * Lives under the working-hours form in the "RDV en ligne" tab: same surface as
 * "how my company organizes" (availability + booking rules). One simple question
 * (chips 15/30/45/60 min) applied to the three travel edges, plus the distance
 * guard-rail. Horizon and rounding are fixed in V1 → shown as a read-only banner.
 */
export function RdvTravelSettingsSection() {
  const {rdvPlanningSetting, isRdvPlanningSettingLoading} = useRdvPlanningSetting();
  const {updateRdvTravelSetting, isUpdatingRdvTravelSetting} = useUpdateRdvTravelSetting();

  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<TravelSettingsFormData>({
    resolver: zodResolver(travelSettingsFormSchema),
    defaultValues: {
      travelTimeMinutes: RDV_DEFAULT_MAX_TRAVEL_TIME_MINUTES,
      maxDistanceKm: null,
    },
    // Reset to the persisted values once the GET resolves (RHF async values sync).
    values: rdvPlanningSetting
      ? {
          travelTimeMinutes: snapToChip(rdvPlanningSetting.maxTimeBetweenEvents),
          maxDistanceKm: rdvPlanningSetting.maxTravelDistanceKm,
        }
      : undefined,
  });

  const {updateRdvProposalLimit, isUpdatingRdvProposalLimit} = useUpdateRdvProposalLimit();
  const proposalLimitForm = useForm<UpdateRdvProposalLimitPayload>({
    resolver: zodResolver(UpdateRdvProposalLimitPayloadSchema),
    defaultValues: {maxRdvProposals: MAX_PROPOSALS},
    values: {maxRdvProposals: rdvPlanningSetting?.maxRdvProposals ?? MAX_PROPOSALS},
  });
  const disabled = isUpdatingRdvTravelSetting || isUpdatingRdvProposalLimit;

  if (isRdvPlanningSettingLoading) {
    return <Spinner size="sm" />;
  }

  return (
    <Form onSubmit={handleSubmit(handleSave)}>
      <h4>Prise de rendez-vous en ligne</h4>
      <p className="text-muted small">
        Les règles appliquées quand un client réserve un créneau en ligne.
      </p>

      <FormGroup floating>
        <Controller
          name="maxRdvProposals"
          control={proposalLimitForm.control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="max-rdv-proposals"
              placeholder="Nombre maximal de créneaux proposés"
              disabled={disabled || !rdvPlanningSetting}
              onChange={(event) => handleProposalLimitChange(Number(event.target.value))}
            >
              {Array.from({length: RDV_PROPOSAL_LIMIT}, (_, index) => index + 1).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="max-rdv-proposals">Nombre maximal de créneaux proposés</Label>
      </FormGroup>

      <FormGroup>
        <Label className="d-block">
          <strong>Temps de trajet maximum acceptable autour d'un rendez-vous</strong>
        </Label>
        <Controller
          name="travelTimeMinutes"
          control={control}
          render={({field}) => (
            <ButtonGroup>
              {MAX_TRAVEL_TIME_MINUTES.map((minutes) => (
                <Button
                  key={minutes}
                  type="button"
                  color="primary"
                  outline={field.value !== minutes}
                  active={field.value === minutes}
                  disabled={disabled}
                  onClick={() => field.onChange(minutes)}
                >
                  {minutes} min
                </Button>
              ))}
            </ButtonGroup>
          )}
        />
        <p className="text-muted small mt-2">
          Appliqué à chaque trajet : de votre point de départ au premier rendez-vous, entre deux
          rendez-vous, et du dernier au retour.
        </p>
      </FormGroup>

      <FormGroup>
        <Label className="d-block">
          <strong>Distance maximale (garde-fou)</strong>
        </Label>
        <FloatingUnitField
          name="maxDistanceKm"
          label="Distance maximale"
          suffix="km"
          control={control}
          disabled={disabled}
          min={1}
          arrowStep={10}
          defaultHint={RDV_DEFAULT_MAX_TRAVEL_DISTANCE_KM}
          error={errors.maxDistanceKm}
        />
        <p className="text-muted small mt-2">
          Aucun créneau n'est proposé au-delà de cette distance. Laissez vide pour utiliser la
          valeur par défaut ({RDV_DEFAULT_MAX_TRAVEL_DISTANCE_KM} km).
        </p>
      </FormGroup>

      <div className="alert alert-info small">
        L'horizon de recherche ({RDV_HORIZON_DAYS} jours) et l'arrondi des créneaux (
        {RDV_ROUNDING_MINUTES} min) sont fixes dans cette version.
      </div>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingRdvTravelSetting ? <Spinner size="sm" /> : null}
        {isUpdatingRdvTravelSetting ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function handleProposalLimitChange(maxRdvProposals: number) {
    try {
      const response = await updateRdvProposalLimit(
        UpdateRdvProposalLimitPayloadSchema.parse({maxRdvProposals})
      );
      proposalLimitForm.setValue("maxRdvProposals", response.maxRdvProposals);
    } catch {
      toast.error("Erreur lors de la sauvegarde du nombre de créneaux");
    }
  }

  async function handleSave(data: TravelSettingsFormData) {
    const minutes = data.travelTimeMinutes;
    try {
      await updateRdvTravelSetting({
        maxTimeFromHome: minutes,
        maxTimeBetweenEvents: minutes,
        maxTimeToHome: minutes,
        maxTravelDistanceKm: data.maxDistanceKm ?? RDV_DEFAULT_MAX_TRAVEL_DISTANCE_KM,
      });
      toast.success("Réglages de prise de rendez-vous enregistrés");
    } catch {
      toast.error("Erreur lors de la sauvegarde des réglages de prise de rendez-vous");
    }
  }
}

/**
 * Snap a persisted minutes value to the nearest allowed chip. Legacy data (the
 * old free-text "temps entre interventions" field) could hold any integer, and
 * null means "never configured" → the V1 default.
 */
function snapToChip(value: number | null): (typeof MAX_TRAVEL_TIME_MINUTES)[number] {
  if (value == null) {
    return RDV_DEFAULT_MAX_TRAVEL_TIME_MINUTES;
  }
  return MAX_TRAVEL_TIME_MINUTES.reduce((nearest, candidate) =>
    Math.abs(candidate - value) < Math.abs(nearest - value) ? candidate : nearest
  );
}
